//#region imports
import { useEffect, useMemo, useState } from 'react';

// nav
import { useHistory } from 'react-router-dom';

// redux
import { setSelectedFlight } from "_redux/slices/flights/flight-information";
import { selectUserDb } from "_redux/slices/main-screen/user";
import { selectDateToISO, selectDepPaxNoShowsFilter } from "_redux/slices/prm-bookings/calendar";
import {
    getInboundFlights,
    getInboundFlightsForecast,
    getOutboundFlights,
    getOutboundFlightsForecast,
    selectFlightStatus,
    selectInboundFlights,
    selectOutboundFlights,
    selectTab, setTab,
    selectOutboundFlightsForecast
} from "_redux/slices/prm-bookings/flights";
import { selectSeasonalVariances } from "_redux/slices/prm-bookings/seasonal-variance";
import { selectSeasonalVarianceFilter } from "_redux/slices/prm-bookings/calendar";

import { useDispatch, useSelector } from 'react-redux';

// bootstrap
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
//styles
import { Spacing } from '_styles';
// custom compoennts
import { Card, CustomSpinner } from "_atoms";
import { DepPaxNoShowsToggle, TableHeader, TableValue, SeasonalVarianceToggle } from "_molecules";
import { ClickableTable } from '_organisms';

// tremor
import { Flex, Subtitle, Toggle, ToggleItem, Title as TremorTitle } from '@tremor/react';

//#endregion

const ForecastTable = (props) => {

    // nav //
    const history = useHistory();

    //#region redux

    const dispatch = useDispatch();
    const userDb = useSelector(selectUserDb);
    const analyticsDate = useSelector(selectDateToISO);                 // date of date picker

    // raw data lists
    const departureList = useSelector(selectOutboundFlights);           // list of outbound flights from bookings db
    const arrivalList = useSelector(selectInboundFlights);              // list of inbound flights from bookings db
    const departureForecast = useSelector(selectOutboundFlightsForecast); // list of outbound forecasted flights from bookings db

    // seasonal variance
    const seasonalVariances = useSelector(selectSeasonalVariances);
    const seasonalVarianceFilter = useSelector(selectSeasonalVarianceFilter);

    const status = useSelector(selectFlightStatus);

    // dep pax no shows filter
    const depPaxNoShowsFilter = useSelector(selectDepPaxNoShowsFilter);

    // arrivals / departures tab
    const tab = useSelector(selectTab);

    //#endregion

    //#region local states
    const [departureListLocal, setDepartureListLocal] = useState([])
    const [arrivalListLocal, setArrivalListLocal] = useState([])
    //#endregion

    //#region functions

    // gets outbound inbound flights + forecasts
    const getFlights = () => {
        //console.log('getting flights...')

        // has all departure flights for the day
        dispatch(getOutboundFlights({
            date: analyticsDate,
            iata: userDb.details.authority,
            type: 'outbound',
            noShows: depPaxNoShowsFilter
        }));
        dispatch(getInboundFlights({
            date: analyticsDate,
            iata: userDb.details.authority,
            type: 'inbound',
            // noShows: depPaxNoShowsFilter
        }));

        // gets all forecasted flights for the day
        dispatch(getInboundFlightsForecast({
            date: analyticsDate,
            iata: userDb.details.authority,
            type: 'inbound',
            // noShows: depPaxNoShowsFilter
        }));
        dispatch(getOutboundFlightsForecast({
            date: analyticsDate,
            iata: userDb.details.authority,
            type: 'outbound',
            noShows: depPaxNoShowsFilter
        }));
    }

    // navigate to selected flight when clicked
    const handleFlightClick = (i) => {

        // use index to find flight that was clicked
        let flight = {};
        if (tab == "outbound") {
            flight = departureList[i];
        }
        else {
            flight = arrivalList[i];
        }

        // console.log('FLIGHT', flight)
        let selectedFlight = {
            flight: flight.flight,
            date: flight.date,
            document: flight && flight.forecastedFlightDoc && flight.forecastedFlightDoc.doc ? flight.forecastedFlightDoc.doc : null,
            type: tab == 'outbound' ? 'Departures' : 'Arrivals'
        }

        //console.log('selected flight ', selectedFlight)

        dispatch(setSelectedFlight(selectedFlight));

        // navigate to flight page
        history.push('/flight-information');

    }

    const generateGraphData = (forecastArray) => {
        if (!Array.isFullArray(forecastArray)) return []
        let tempForecast = [...forecastArray]
        tempForecast?.forEach((forecast) => {
            if (seasonalVariances && seasonalVariances.seasonalAdjustments && seasonalVarianceFilter) {
                // // seasonal variances exist, check each one
                seasonalVariances.seasonalAdjustments.forEach((variance) => {
                    // get date range of seasonal variance
                    let startDate = new Date(variance.startDate);
                    let endDate = new Date(variance.endDate);
                    let forecastDate = new Date(analyticsDate);
                    if (forecastDate >= startDate && forecastDate <= endDate) {
                        forecast.forecastedCount = Math.round(forecast?.forecastedCount + (forecast?.forecastedCount * variance.multiplier));
                    } else {
                        forecast.forecastedCount = Math.round(forecast.forecastedCount)
                    }
                })
            } else {
                forecast.forecastedCount = Math.round(forecast.forecastedCount)
            }
        })
        return tempForecast
    }

    //#endregion

    //#region use effects

    // get flights on render, dep pax filter change / date change
    useEffect(() => {
        getFlights();
    }, [analyticsDate]);

    // calculate seasonal variance
    useEffect(() => {

        let tempDepartureForecast = generateGraphData(departureList)
        let tempArrivalForecast = generateGraphData(arrivalList)
        setDepartureListLocal(Array.isFullArray(tempDepartureForecast) ? tempDepartureForecast : [])
        setArrivalListLocal(Array.isFullArray(tempArrivalForecast) ? tempArrivalForecast : [])

    }, [seasonalVariances, departureList, arrivalList, seasonalVarianceFilter])

    //#endregion

    //#region review table functions and columns
    const columns = useMemo(
        () => [
            {
                Header: <TableHeader title={'Flight'} />,
                accessor: "flight",
                Cell: ({ cell: { value } }) => <TableValue title={value} />
            },
            {
                Header: <TableHeader title={'Passengers'} />,
                accessor: "passengerCount",
                Cell: ({ cell: { value } }) => <TableValue title={value} />
            },
            {
                Header: <TableHeader title={'Forecasted'} />,
                accessor: "forecastedCount",
                Cell: ({ cell: { value } }) => <TableValue title={value == undefined || value == null ? 'Missing' : value} textColor={value == undefined || value == null ? 'red' : 'green'} />
            },
        ],
        []
    );
    //#endregion

    useEffect(() => {
        //console.log('departureList', departureList)
        //console.log('departureForecast', departureForecast)
    }, [departureList])

    useEffect(() => {
        //console.log('local state', departureListLocal)
    }, [departureForecast])


    useEffect(() => {
        // console.log('local state', departureListLocal)
    }, [departureListLocal])

    return (
        <Row className='mt-4' style={{ marginBottom: Spacing.SCALE_22 }}>

            <Col>
                <Card>

                    {/* table toolbar */}
                    <div>

                        <div style={{ display: 'flex' }}>

                            {/* no shows toggle */}
                            <DepPaxNoShowsToggle />
                            <div style={{ marginInlineStart: "10px" }}>
                                <SeasonalVarianceToggle />
                            </div>

                            {/* loading */}
                            {status == 'loading' && <CustomSpinner size={'small'} style={{ marginLeft: Spacing.SCALE_12, marginTop: Spacing.SCALE_4 }} />}
                        </div>

                        <Flex>

                            {/* table title */}
                            <div style={{ marginBottom: Spacing.SCALE_8 }}>
                                <TremorTitle>Flight Schedule Companion</TremorTitle>
                                <Subtitle>{'View the flights that are departing and arriving into your airport, the number of passengers per flight and if the flight was included in the forecasted'}</Subtitle>
                            </div>

                            {/* arrivals / departures toggle */}
                            <Col style={{ display: "flex", justifyContent: "flex-end", marginRight: 12 }}>
                                <Toggle color="zinc" defaultValue={tab} onValueChange={(value) => dispatch(setTab(value))}>
                                    <ToggleItem value={"outbound"} text="Departures" />
                                    <ToggleItem value={"inbound"} text="Arrivals" />
                                </Toggle>
                            </Col>
                        </Flex>
                    </div>

                    <div style={{ display:'flex' }}>
                        <div>
                            <p>Departure Flights: {departureListLocal.length + 1}</p>
                        </div>
                        <div style={{ marginLeft:Spacing.SCALE_12}}>
                            <p>Arrival Flights: {arrivalListLocal.length + 1}</p>
                        </div>
                    </div>

                    {/* table */}
                    <div style={{ overflowY: 'scroll', height: '40em' }}>
                        <ClickableTable
                            columns={columns}
                            data={tab == 'outbound' ? departureListLocal : arrivalListLocal}
                            clickable={true}
                            handleClick={(i) => { handleFlightClick(i) }}
                            status={status}
                        />
                    </div>
                </Card>
            </Col>
        </Row>
    );
};

export default ForecastTable;
